import {useCallback, useMemo, useState} from 'react';
import {useQuotesFirst} from '@/matic/features/quoting/hooks/useQuotingAvailability';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {
  ChevronLeft,
  Home,
  ListChecks,
  PanelLeft,
  Search,
  Settings,
} from 'lucide-react';
import {useSidebarStore} from './sidebarStore';
import {useNavConfig, type NavItemConfig} from './navConfig';
import NavItem from './NavItem';
import RelayConfigSidebarNav from './RelayConfigSidebarNav';
import {GlobalCreateFab} from './GlobalCreateFab';
import {canUseRelayView} from '@/matic/config/features';
import {env} from '@/matic/config/env';
import {cn} from '@/matic/lib/utils/cn';
import {QuoteResourcesMenu} from '@/matic/features/quoting/components/QuoteResourcesMenu';
import {QuoteSidebarList} from '@/matic/features/quoting/components/QuoteSidebarList';
import {SegmentedControl} from '@/matic/components/ui/segmented-control';
import {SlideSwap} from '@/matic/components/ui/slide-swap';
import {type UiMode, useUiMode} from '@/matic/features/_shared/hooks/useUiMode';
import {RELAY_CONFIG_PATH} from '@/matic/features/relay/components/config/relayConfigSections';
import {usePendingApprovals} from '@/matic/features/vault/approvals/hooks/usePendingApprovals';
import {useMyAssignmentsQuery} from '@/matic/features/training/my-training/hooks/useMyAssignmentsQuery';
import {useMyPendingAttestationsQuery} from '@/matic/features/training/my-training/hooks/useMyPendingAttestationsQuery';
import {useMyPendingInstructorSignoffsQuery} from '@/matic/features/training/my-training/hooks/useMyPendingInstructorSignoffsQuery';
import {useTriageCount} from '@/matic/features/relay/hooks/useTriageCount';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {useTenant} from '@/matic/features/_shared/hooks/useTenant';
import {RelayForwardingAddressCard} from '@/matic/features/relay/components/shared/RelayForwardingAddressCard';
import {getRelayForwardingAddress} from '@/matic/features/relay/utils/forwarding-address';
import {LibraryRail} from '@/matic/features/relay/components/library/rail/LibraryRail';
import {LibraryGlobalSearchDialog} from '@/matic/features/relay/components/library/LibraryGlobalSearchDialog';

import UserProfile from '../UserProfile';

export default function SidebarNav() {
  // The AES shell follows the tenant's engine; its frame only renders once that's known.
  const quotesFirst = useQuotesFirst() === true;
  const closeSidebar = useSidebarStore((state) => state.close);
  const openSidebar = useSidebarStore((state) => state.open);
  const navSections = useNavConfig();
  const tenantId = useRequiredTenantId();
  const {mode: uiMode, setMode: setUiMode} = useUiMode();
  const [librarySearchOpen, setLibrarySearchOpen] = useState(false);
  const {pathname} = useLocation();
  const navigate = useNavigate();

  // QMS ↔ Relay view switch — tenants with Relay-view access (dev + TechComm).
  // Lives at the bottom of the expanded sidebar (collapsed sidebar omits it).
  const showUiModeToggle =
    !quotesFirst && Boolean(tenantId && canUseRelayView(tenantId));
  const handleUiModeChange = (next: UiMode) => {
    setUiMode(next);
    // Relay's rail IS the primary surface, so enter it expanded.
    if (next === 'otto') openSidebar();
    navigate(next === 'otto' ? '/home' : '/');
  };

  const relayForwardingAddress =
    uiMode === 'otto' ? getRelayForwardingAddress(tenantId) : undefined;

  // While on the relay-config surface, the sidebar body swaps to the
  // config sections (Connections / Org Knowledge / Configuration),
  // replacing the normal nav until the user backs out.
  const isRelayConfig = pathname.startsWith(RELAY_CONFIG_PATH);

  // Clicking empty space anywhere in the expanded sidebar (not on a nav
  // link/button) collapses it, matching the chevron button. The
  // target === currentTarget check ensures this only fires when the click
  // lands directly on the container, not when it bubbles up from a child.
  const handleEmptySpaceClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (event.target === event.currentTarget) closeSidebar();
    },
    [closeSidebar]
  );
  const {data: tenant} = useTenant(tenantId);

  // Fetch pending tasks count
  const {data: approvals} = usePendingApprovals(tenantId);
  const {data: assignmentsData} = useMyAssignmentsQuery();
  const triageCount = useTriageCount();

  // Pending instructor actions — drive the ``My Training`` badge
  // (and the parent ``TRAINING`` icon when the sidebar is collapsed)
  // so the user sees a count without having to open the page.
  const {data: pendingInstructorSignoffs} =
    useMyPendingInstructorSignoffsQuery();
  const {data: pendingSecondaryAttestations} = useMyPendingAttestationsQuery();

  // Inject pending count badges into nav items
  const sectionsWithBadges = useMemo(() => {
    const approvalCount = approvals?.total ?? 0;
    const assignmentCount =
      assignmentsData?.items.filter((a) => a.status !== 'completed').length ??
      0;
    const totalPendingCount = approvalCount + assignmentCount;
    const approvalsCount =
      (pendingInstructorSignoffs?.length ?? 0) +
      (pendingSecondaryAttestations?.length ?? 0);
    const approvalsBadge =
      approvalsCount > 0
        ? approvalsCount > 99
          ? '99+'
          : String(approvalsCount)
        : undefined;

    // Tag both the ``My Training`` leaf and the ``TRAINING`` parent
    // so the count is visible whether the dropdown is collapsed
    // (parent badge) or expanded (leaf badge). When TRAINING is
    // itself a leaf (non-admin users), the parent path carries the
    // badge directly.
    const applyApprovalsBadge = (item: NavItemConfig): NavItemConfig => {
      if (item.label === 'My Training' && approvalsBadge) {
        return {...item, badge: approvalsBadge};
      }

      if (item.label === 'TRAINING') {
        const merged: NavItemConfig = item.children
          ? {
              ...item,
              children: item.children.map((child) =>
                applyApprovalsBadge(child)
              ),
            }
          : {...item};
        if (approvalsBadge) merged.badge = approvalsBadge;
        return merged;
      }

      return item;
    };

    return navSections.map((section) => ({
      ...section,
      items: section.items.map((item) => {
        if (item.path === '/notifications' && totalPendingCount > 0) {
          return {
            ...item,
            badge: totalPendingCount > 99 ? '99+' : String(totalPendingCount),
          };
        }

        if (item.path === '/inbox' && triageCount > 0) {
          return {
            ...item,
            badge: triageCount > 99 ? '99+' : String(triageCount),
          };
        }

        return applyApprovalsBadge(item);
      }),
    }));
  }, [
    navSections,
    approvals,
    assignmentsData,
    triageCount,
    pendingInstructorSignoffs,
    pendingSecondaryAttestations,
  ]);

  if (quotesFirst) {
    // The AES shell, as the desk's wireframe draws it: the ribbon's lead (sidebar
    // toggle, Home) sits over the rail as the first 40px of one contiguous bar; below
    // it the queue, one quote per line; at the foot Resources and the person. No app
    // name, no tenant strip, no Relay surfaces, no Settings.
    return (
      <div
        className="flex h-full w-full flex-col bg-sidebar-bg"
        onClick={handleEmptySpaceClick}
      >
        <div className="flex h-10 shrink-0 items-center gap-1 border-b border-sidebar-border pl-2">
          <button
            type="button"
            title="Toggle sidebar (Ctrl+B)"
            aria-label="Collapse sidebar"
            onClick={closeSidebar}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sidebar-fg/65 transition-colors hover:bg-sidebar-hover hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
          >
            <PanelLeft className="h-[17px] w-[17px]" />
          </button>
          <Link
            to="/"
            className={cn(
              'flex h-7 items-center gap-2 rounded-md px-2.5 text-[13px] font-medium no-underline transition-colors hover:bg-sidebar-hover hover:text-white',
              pathname === '/'
                ? 'bg-sidebar-active text-white'
                : 'text-sidebar-fg/75'
            )}
          >
            <Home className="h-[15px] w-[15px]" />
            <span>Home</span>
          </Link>
          <Link
            to="/quotes"
            className={cn(
              'flex h-7 items-center gap-2 rounded-md px-2.5 text-[13px] font-medium no-underline transition-colors hover:bg-sidebar-hover hover:text-white',
              pathname === '/quotes'
                ? 'bg-sidebar-active text-white'
                : 'text-sidebar-fg/75'
            )}
          >
            <ListChecks className="h-[15px] w-[15px]" />
            <span>Quotes</span>
          </Link>
        </div>
        <div
          className="flex min-h-0 flex-1 flex-col overflow-y-auto border-r border-sidebar-border px-2 py-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-sidebar-active/50 hover:[&::-webkit-scrollbar-thumb]:bg-sidebar-active [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5"
          onClick={handleEmptySpaceClick}
        >
          <QuoteSidebarList />
        </div>
        <div className="shrink-0 border-r border-t border-sidebar-border px-2 py-2">
          <QuoteResourcesMenu />
          <div className="px-1 pt-1">
            <UserProfile variant="sidebar" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-full w-full flex-col border-r border-sidebar-border bg-sidebar-bg"
      onClick={handleEmptySpaceClick}
    >
      {/* Sidebar Header: MATIC returns to the Home landing. */}
      <div className="flex h-16 w-full shrink-0 items-center border-b border-sidebar-border">
        <Link
          to="/home"
          aria-label="Go to Home"
          className="flex h-full flex-1 items-center px-6 text-2xl font-bold text-white no-underline transition-colors hover:text-primary-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
        >
          {env.APP_NAME}
        </Link>
        <button
          type="button"
          aria-label="Collapse sidebar"
          onClick={closeSidebar}
          className="flex h-full shrink-0 items-center justify-center px-2 text-sidebar-fg transition-colors hover:bg-sidebar-hover hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
        >
          <ChevronLeft className="h-5 w-5 scale-y-125" />
        </button>
      </div>

      {/* Tenant name strip */}
      {tenant?.name && (
        <div className="flex shrink-0 items-center gap-2 px-6 py-2">
          <span className="min-w-0 flex-1 truncate text-sm text-primary-500">
            {tenant.name}
          </span>
          {uiMode === 'otto' && (
            <button
              type="button"
              onClick={() => {
                setLibrarySearchOpen(true);
              }}
              title="Search"
              aria-label="Search Library"
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-sidebar-fg/55 transition-colors hover:bg-white/5 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
            >
              <Search className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Navigation body: normal nav cross-slides to the relay-config
          section nav when the user enters the relay-config surface. */}
      <SlideSwap
        className="flex-1"
        showSecondary={isRelayConfig}
        secondary={<RelayConfigSidebarNav />}
        primary={
          uiMode === 'otto' ? (
            // Relay mode: the consolidated Library rail (browse + conversations)
            // replaces the classic nav-section list entirely.
            <LibraryRail onEmptySpaceClick={handleEmptySpaceClick} />
          ) : (
            <nav
              className="flex-1 overflow-y-auto py-4 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-sidebar-active/50 hover:[&::-webkit-scrollbar-thumb]:bg-sidebar-active [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5"
              onClick={handleEmptySpaceClick}
            >
              {sectionsWithBadges.map((section, index) => (
                <div key={index} className="mb-6">
                  {section.title && (
                    <div className="mb-2 px-6 text-xs font-semibold uppercase tracking-wider text-sidebar-fg/50">
                      {section.title}
                    </div>
                  )}
                  <div className="flex flex-col gap-y-1">
                    {section.items.map((item) => (
                      <NavItem key={item.label} item={item} />
                    ))}
                  </div>
                </div>
              ))}
            </nav>
          )
        }
      />

      {relayForwardingAddress && (
        <RelayForwardingAddressCard address={relayForwardingAddress} />
      )}

      {uiMode === 'otto' && (
        <Link
          to="/admin/relay-config"
          className="flex shrink-0 items-center gap-2.5 px-4 py-1.5 text-sm font-medium text-sidebar-fg/55 no-underline transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
        >
          <Settings className="h-4 w-4 shrink-0" />
          <span className="flex-1">Settings</span>
        </Link>
      )}

      {/* QMS ↔ Relay view switch — dev tenants, expanded sidebar only.
          Deliberately de-emphasized: a secondary escape hatch, not a primary
          control. */}
      {showUiModeToggle && (
        <div className="shrink-0 px-4 pb-4 pt-2">
          <SegmentedControl<UiMode>
            ariaLabel="UI view mode"
            value={uiMode}
            onChange={handleUiModeChange}
            options={[
              {value: 'classic', label: 'QMS'},
              {value: 'otto', label: 'Relay'},
            ]}
            variant="dark"
            className="w-full"
          />
        </div>
      )}

      <div className="border-t border-sidebar-border bg-sidebar-bg px-3 py-2">
        <UserProfile variant="sidebar" />
      </div>

      <GlobalCreateFab />
      {librarySearchOpen && (
        <LibraryGlobalSearchDialog
          open={librarySearchOpen}
          onOpenChange={setLibrarySearchOpen}
        />
      )}
    </div>
  );
}
